/**
 * EmailVerificationModal — Step 2 of the signup wizard.
 *
 * 6-digit email verification gate. Opens AFTER the Security Check
 * (captcha) and BEFORE the Application Password modal.
 *
 * On mount: auto-POSTs /auth/signup/send-verification so the user
 * doesn't have to click "Send code".
 *
 * On Verify: POSTs /auth/signup/verify-code with the typed code.
 * On success: hands the parent a single-use `verification_token`
 * that's later attached to /auth/register so the account is NOT
 * created until this token is consumed.
 *
 * Visual chrome is provided by <SignupWizardShell> so all 3 steps
 * share the same look.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Mail, Loader2 } from 'lucide-react';
import axios from 'axios';
import SignupWizardShell from './SignupWizardShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailVerificationModal({
  open,
  email,
  firstName = '',
  onVerified,   // ({ verification_token })
  onClose,
}) {
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sentAt, setSentAt] = useState(0);
  const [error, setError] = useState('');
  const [secondsToResend, setSecondsToResend] = useState(0);
  const sentRef = useRef(false);

  const sendCode = async (resend = false) => {
    if (!email) return;
    setSending(true);
    setError('');
    try {
      await axios.post(`${API}/auth/signup/send-verification`, { email, first_name: firstName });
      setSentAt(Date.now());
      setSecondsToResend(30);
      if (resend) toast.success('Code resent');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not send verification email';
      setError(detail);
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (open && !sentRef.current) {
      sentRef.current = true;
      sendCode(false);
    }
    if (!open) {
      sentRef.current = false;
      setCode('');
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (secondsToResend <= 0) return;
    const t = setInterval(() => setSecondsToResend((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [secondsToResend]);

  const onVerify = async () => {
    if (code.length !== 6) {
      setError('Enter the 6-digit code from your email');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/auth/signup/verify-code`, { email, code });
      onVerified({ verification_token: data.verification_token });
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not verify code';
      setError(detail);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <SignupWizardShell
      open={open}
      step={2}
      icon={<Mail className="w-6 h-6" />}
      onClose={onClose}
      testId="email-verification-modal"
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-700">
          We sent a 6-digit code to <span className="font-medium text-slate-900">{email}</span>. Enter it below to continue. The code expires in 10 minutes.
        </p>

        <div className="space-y-1.5">
          <Label className="text-xs text-slate-700">6-digit code</Label>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="123456"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoComplete="one-time-code"
            autoFocus
            className="font-mono text-lg text-center tracking-widest bg-white text-slate-900"
            data-testid="verify-code-input"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 p-2 rounded" data-testid="verify-code-error">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>
            {sending ? (
              <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Sending…</span>
            ) : sentAt ? (
              'Code sent. Check your inbox.'
            ) : null}
          </span>
          <button
            type="button"
            onClick={() => sendCode(true)}
            disabled={sending || secondsToResend > 0}
            className="underline disabled:no-underline disabled:opacity-50 text-[#586D52]"
            data-testid="verify-code-resend"
          >
            {secondsToResend > 0 ? `Resend in ${secondsToResend}s` : 'Resend code'}
          </button>
        </div>

        <Button
          type="button"
          className="w-full bg-[#586D52] hover:bg-[#465A41] text-white h-11"
          onClick={onVerify}
          disabled={verifying || code.length !== 6}
          data-testid="verify-code-submit"
        >
          {verifying ? 'Verifying…' : 'Verify & Continue'}
        </Button>
      </div>
    </SignupWizardShell>
  );
}
